import {
  APIEventTarget,
  APIObject,
  IsolatedPaginatedListOperation,
  PaginatedListOperationOptions,
  RetrieveMultipleOperation,
  RetrieveOperation,
  RetrieveOperationOptions,
} from '@rokoli/bnb'
import {
  APIRetrieveAggregator,
  BaseRequestOptions,
  ExtendableAPI,
  useObject,
  usePaginatedList,
} from '@rokoli/bnb/drf'
import { tryOnScopeDispose } from '@vueuse/core'
import { isEqual } from 'es-toolkit'
import {
  computed,
  isReadonly,
  MaybeRefOrGetter,
  readonly,
  ref,
  Ref,
  toRef,
  toValue,
  watch,
  watchEffect,
} from 'vue'

type APIRetrieveAggregatorOptions = Parameters<typeof APIRetrieveAggregator>[1]
const AGGREGATE_MAX_QUEUE_COUNT: number = 100

export function aggregateWithIdsParameter<T extends APIObject>(
  list: IsolatedPaginatedListOperation<T>,
  config: APIRetrieveAggregatorOptions | undefined = undefined,
) {
  const configuredMaxQueueCount = Math.round(config?.maxQueueCount ?? AGGREGATE_MAX_QUEUE_COUNT)
  const queueCount = Math.min(configuredMaxQueueCount, AGGREGATE_MAX_QUEUE_COUNT)
  if (configuredMaxQueueCount > queueCount) {
    console.warn(
      `Maximum queue count is capped at ${AGGREGATE_MAX_QUEUE_COUNT}. You’ve specified ${configuredMaxQueueCount}.`,
    )
  }

  return APIRetrieveAggregator<T>(
    async (ids, options) => {
      const { items } = await list(1, {
        limit: queueCount,
        requestInit: options?.requestInit,
        query: new URLSearchParams({ ids: ids.join(',') }),
      })
      return items
    },
    { ...(config ?? {}), maxQueueCount: queueCount },
  )
}

export interface Ordering<T extends APIObject> {
  id: T['id']
  order: number
}

export type APIReorderOperation<T extends APIObject> = (
  orderings: Ordering<T>[],
  options?: BaseRequestOptions | undefined,
) => Promise<void>

export function APIReorder<T extends APIObject>(api: ExtendableAPI<T>) {
  const reorder: APIReorderOperation<T> = async (orderings, options = undefined) => {
    const res = await fetch(
      api.createRequest(api.endpoint('reorder'), options?.requestInit, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderings }),
      }),
    )
    await api.maybeThrowResponse(res)
  }

  return { reorder }
}

export function useOrdering<T extends APIObject>(
  data: MaybeRefOrGetter<T[]>,
  options: { onChange: (orderings: Ordering<T>[]) => unknown } | undefined = undefined,
) {
  const getOrderedIds = (data: T[]) => toValue(data).map((item) => item.id)
  const orderedIds = ref(getOrderedIds(toValue(data)))
  const orderedData = computed(() => {
    const _data = new Map(toValue(data).map((item) => [item.id, item]))
    const result: T[] = []
    for (const id of orderedIds.value) {
      if (_data.has(id)) {
        result.push(_data.get(id) as T)
        _data.delete(id)
      }
    }
    result.push(..._data.values())
    return result
  })

  function move(id: T['id'], direction: 'up' | 'down') {
    const ids = Array.from(orderedIds.value)
    const index = ids.indexOf(id)

    if (
      index === -1 ||
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === ids.length - 1)
    )
      return

    ids.splice(index, 1)
    if (direction === 'up') {
      ids.splice(index - 1, 0, id)
    } else {
      ids.splice(index + 1, 0, id)
    }
    orderedIds.value = ids
  }

  function up(id: T['id']) {
    move(id, 'up')
  }

  function down(id: T['id']) {
    move(id, 'down')
  }

  const orderWatcher = watch(
    () => getOrderedIds(toValue(data)),
    (newOrder) => (orderedIds.value = newOrder),
  )

  const changeWatcher = watch(orderedIds, (newIds) => {
    if (!isEqual(getOrderedIds(toValue(data)), newIds)) {
      void options?.onChange(newIds.map((id, index) => ({ id, order: index })))
    }
  })

  tryOnScopeDispose(() => {
    orderWatcher.stop()
    changeWatcher.stop()
  })

  return { orderedIds, orderedData, move, up, down }
}

type UseObjectListFromStoreStoreType<T extends APIObject> = {
  itemMap: MaybeRefOrGetter<Map<T['id'], T>>
  retrieveMultiple: RetrieveMultipleOperation<T>
}
export function useObjectListFromStore<T extends APIObject>(
  ids: Ref<T['id'][]>,
  store: UseObjectListFromStoreStoreType<T>,
): { isLoading: Readonly<Ref<boolean>>; objects: Ref<T[]> }
export function useObjectListFromStore<T extends APIObject>(
  ids: Readonly<Ref<T['id'][]>> | (() => T['id'][]) | T['id'][],
  store: UseObjectListFromStoreStoreType<T>,
): { isLoading: Readonly<Ref<boolean>>; objects: Readonly<Ref<T[]>> }
export function useObjectListFromStore<T extends APIObject>(
  ids: MaybeRefOrGetter<T['id'][]>,
  store: UseObjectListFromStoreStoreType<T>,
) {
  const itemMap = toRef(store.itemMap)
  const idsRef = toRef(ids) as Ref<T['id'][]> | Readonly<Ref<T['id'][]>>
  const isLoading = ref(false)
  const objects = computed({
    get() {
      const result = toValue(idsRef)
        .map((id) => itemMap.value.get(id))
        .filter((item) => item !== undefined)
      return result as T[]
    },
    set(value: T[]) {
      if (!isReadonly(idsRef)) (idsRef as Ref<T['id'][]>).value = value.map((obj) => obj.id)
    },
  })
  watchEffect(async () => {
    isLoading.value = true
    try {
      void (await store.retrieveMultiple(toValue(ids), { useCached: true }))
    } finally {
      isLoading.value = false
    }
  })
  return {
    objects,
    isLoading: readonly(isLoading),
  }
}

type UseRetrieveHelperStoreType<T extends APIObject> = {
  itemMap: MaybeRefOrGetter<Map<T['id'], T>>
  retrieve: RetrieveOperation<T>
  retrieveMultiple: RetrieveMultipleOperation<T>
}

export function useRetrieveHelpers<T extends APIObject>(store: UseRetrieveHelperStoreType<T>) {
  function use(
    id: MaybeRefOrGetter<T['id'] | null | undefined>,
    options?: RetrieveOperationOptions | undefined,
  ) {
    return useObject(id, store.itemMap, store.retrieve, options)
  }

  function useList(
    ids:
      | Ref<T['id'][]>
      | (Readonly<Ref<T['id'][]>> | (() => T['id'][]) | T['id'][])
      | MaybeRefOrGetter<T['id'][]>,
  ) {
    return useObjectListFromStore(ids, store)
  }

  return { use, useList }
}

type UseListHelpersStoreType<T extends APIObject> = {
  listIsolated: IsolatedPaginatedListOperation<T>
  events?: APIEventTarget<T> | undefined
}

export function useListHelpers<T extends APIObject>(store: UseListHelpersStoreType<T>) {
  function paginate(
    page: MaybeRefOrGetter<number>,
    limit: MaybeRefOrGetter<number>,
    options?:
      | {
          query?: MaybeRefOrGetter<URLSearchParams | undefined>
          noPageAutoReset?: boolean
          requestInit?: RequestInit | undefined
        }
      | undefined,
  ) {
    return usePaginatedList(store.listIsolated, page, limit, { events: store.events, ...options })
  }

  return { paginate }
}

export function useStoreHelpers<T extends APIObject>(
  store: UseRetrieveHelperStoreType<T> & UseListHelpersStoreType<T>,
) {
  return { ...useRetrieveHelpers(store), ...useListHelpers(store) }
}

type ConsumeOperation<T extends APIObject> = (
  options?: PaginatedListOperationOptions | undefined,
) => Promise<T[]>
export function APIConsume<T extends APIObject>(list: IsolatedPaginatedListOperation<T>) {
  const consume: ConsumeOperation<T> = async (
    options: PaginatedListOperationOptions | undefined = undefined,
  ) => {
    const result: T[] = []
    let page = 1

    while (true) {
      const data = await list(page, { limit: 1_000_000, ...options })
      for (const item of data.items) {
        result.push(item)
      }
      if (data.numberOfPages <= page) break
      else page++
    }

    return result
  }

  return { consume }
}

type GenericDetailActionOptions = {
  requestInit?: RequestInit
  noStore?: boolean
}

export function APIGenericDetailAction<T extends APIObject>(
  api: ExtendableAPI<T>,
  action: string,
  actionOptions?: GenericDetailActionOptions,
) {
  return async function (id: T['id'], options?: BaseRequestOptions | undefined) {
    const query = new URLSearchParams(options?.query ?? {})
    const res = await fetch(
      api.createRequest(api.endpoint(id, action, query), options?.requestInit, {
        method: 'POST',
        ...actionOptions?.requestInit,
      }),
    )
    await api.maybeThrowResponse(res)
    const result = await res.json()
    if (actionOptions?.noStore !== true) {
      api.itemMap.value.set(id, result)
    }
    return result
  }
}
